import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  FiGrid,
  FiSearch,
  FiMapPin,
  FiPhone,
  FiMail,
  FiHeart,
  FiCalendar,
  FiCheckCircle,
  FiX,
  FiArrowRight,
  FiUser,
  FiLayers,
  FiRefreshCw,
  FiInfo,
} from "react-icons/fi";
import {
  getServiceCategories,
  getServiceProvidersByCategory,
  getServicesByUser,
  getServiceDetails,
  toggleServiceFavorite,
  createMeetingReminder,
} from "../../../api/customer/customerFeatureApis";

export default function CustomerServicesDirectory() {
  const authState = useSelector((state) => state.auth);
  const token = authState?.user?.token || authState?.token || authState?.user?.refreshToken;

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categorySearch, setCategorySearch] = useState("");

  const [searchParams] = useSearchParams();
  const initialCategoryId = searchParams.get("category");
  const [providers, setProviders] = useState([]);
  const [providerSearch, setProviderSearch] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterCity, setFilterCity] = useState("");

  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerServices, setProviderServices] = useState([]);

  // Detail Modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [serviceDetail, setServiceDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  // Meeting Reminder Modal
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderTitle, setReminderTitle] = useState("");
  const [reminderDate, setReminderDate] = useState("");
  const [reminderTime, setReminderTime] = useState("");
  const [reminderNotes, setReminderNotes] = useState("");
  const [reminderSubmitting, setReminderSubmitting] = useState(false);

  const [loading, setLoading] = useState(false);
  const [alertMsg, setAlertMsg] = useState(null);

  // Fetch Categories
  const fetchCategories = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await getServiceCategories(token, categorySearch);
      const catList = data?.allServiceCategories || [];
      const activeCats = catList.filter((c) => c.active !== false && c.deleted !== true);
      setCategories(activeCats.length > 0 ? activeCats : catList);
      if (activeCats.length > 0 && !selectedCategory) {
        // ?category=<id> comes from the Overview "Home Services" section
        const requested = activeCats.find((c) => String(c.id) === initialCategoryId);
        setSelectedCategory(requested || activeCats[0]);
      }
    } catch (err) {
      console.error("Failed to load service categories:", err);
    } finally {
      setLoading(false);
    }
  }, [token, categorySearch, selectedCategory]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Fetch Providers when selected category changes or search filters apply
  const fetchProviders = useCallback(async () => {
    if (!token || !selectedCategory?.id) return;
    setLoading(true);
    try {
      const data = await getServiceProvidersByCategory(token, {
        categoryId: selectedCategory.id,
        searchText: providerSearch,
        filterState,
        filterCity,
      });
      setProviders(data?.users || []);
    } catch (err) {
      console.error("Failed to load service providers:", err);
    } finally {
      setLoading(false);
    }
  }, [token, selectedCategory, providerSearch, filterState, filterCity]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  // Load services for a provider
  const handleSelectProvider = async (provider) => {
    setSelectedProvider(provider);
    setLoading(true);
    try {
      const data = await getServicesByUser(token, provider.id);
      setProviderServices(data?.allServices || data?.services || []);
    } catch (err) {
      console.error("Failed to fetch provider services:", err);
    } finally {
      setLoading(false);
    }
  };

  // Open Service Detail Modal
  const handleOpenDetail = async (serviceId) => {
    setShowDetailModal(true);
    setDetailLoading(true);
    try {
      const data = await getServiceDetails(token, serviceId);
      setServiceDetail(data?.allService || data?.service || null);
    } catch (err) {
      console.error("Failed to fetch service detail:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Toggle Favorite
  const handleFavoriteToggle = async () => {
    if (!serviceDetail?.id) return;
    setFavoriteLoading(true);
    try {
      const isFav = serviceDetail.is_favorite || serviceDetail.favorite;
      const favId = serviceDetail.favorite_service_id || serviceDetail.favorite_id;
      const res = await toggleServiceFavorite(token, serviceDetail.id, isFav, favId);
      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        setServiceDetail((prev) => ({
          ...prev,
          is_favorite: !isFav,
          favorite_service_id: isFav ? null : res.favorite_service_id || res.id,
        }));
      }
    } catch (err) {
      console.error("Favorite toggle failed:", err);
    } finally {
      setFavoriteLoading(false);
    }
  };

  // Schedule Reminder Submit
  const handleScheduleReminder = async (e) => {
    e.preventDefault();
    if (!reminderDate || !reminderTime || !reminderTitle) {
      alert("Please enter title, date, and time for the reminder.");
      return;
    }
    setReminderSubmitting(true);
    try {
      const payload = {
        title: reminderTitle,
        meeting_date: reminderDate,
        meeting_time: reminderTime,
        note: reminderNotes,
        all_services_id: serviceDetail?.id || 0,
        property_id: 0,
        local_bazaar_id: 0,
        interiror_design_id: 0,
      };
      const res = await createMeetingReminder(token, payload);
      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        setAlertMsg({ type: "success", text: "Meeting reminder created successfully!" });
        setShowReminderModal(false);
        setReminderTitle("");
        setReminderDate("");
        setReminderTime("");
        setReminderNotes("");
      } else {
        alert(res?.text || "Failed to schedule reminder.");
      }
    } catch (err) {
      alert(err?.response?.data?.text || "Failed to schedule reminder.");
    } finally {
      setReminderSubmitting(false);
    }
  };

  return (
    <div className="container-fluid px-3 px-md-4 py-3">
      {/* Page Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
        <div>
          <h3 className="fw-bold mb-1 d-flex align-items-center gap-2 text-dark">
            <FiLayers className="text-primary" /> Verified Home Services Directory
          </h3>
          <p className="text-muted mb-0 small">
            Explore trusted home improvement professionals, architects, contractors, and specialists.
          </p>
        </div>
        <button
          className="btn btn-outline-secondary d-flex align-items-center gap-1 btn-sm"
          onClick={() => {
            fetchCategories();
            fetchProviders();
          }}
          disabled={loading}
        >
          <FiRefreshCw className={loading ? "spin-icon" : ""} /> Refresh
        </button>
      </div>

      {alertMsg && (
        <div
          className={`alert alert-${alertMsg.type} alert-dismissible fade show d-flex align-items-center justify-content-between mb-3`}
          role="alert"
        >
          <span>{alertMsg.text}</span>
          <button
            type="button"
            className="btn-close"
            onClick={() => setAlertMsg(null)}
          />
        </div>
      )}

      {/* Categories Horizontal Selector */}
      <div className="card border-0 shadow-sm rounded-3 mb-4">
        <div className="card-body p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-semibold text-secondary small text-uppercase">Service Categories</span>
            <div className="input-group input-group-sm" style={{ maxWidth: "250px" }}>
              <span className="input-group-text bg-white border-end-0">
                <FiSearch size={14} className="text-muted" />
              </span>
              <input
                type="text"
                className="form-control border-start-0"
                placeholder="Search categories..."
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
              />
            </div>
          </div>

          <div className="d-flex gap-2 overflow-auto pb-2 pt-1">
            {categories.map((cat) => {
              const isSelected = selectedCategory?.id === cat.id;
              return (
                <button
                  key={cat.id}
                  className={`btn btn-sm text-nowrap rounded-pill px-3 py-1 d-flex align-items-center gap-2 transition-all ${
                    isSelected
                      ? "btn-primary shadow-sm"
                      : "btn-outline-light text-dark border bg-white"
                  }`}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setSelectedProvider(null);
                    setProviderServices([]);
                  }}
                >
                  <FiGrid size={13} />
                  <span>{cat.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content Area: Providers List / Services */}
      <div className="row g-4">
        {/* Left Column: Providers in Category */}
        <div className="col-12 col-lg-5">
          <div className="card border-0 shadow-sm rounded-3">
            <div className="card-header bg-white border-bottom p-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="fw-bold mb-0 text-dark">
                  {selectedCategory?.name || "Services"} Providers
                </h6>
                <span className="badge bg-primary bg-opacity-10 text-primary">
                  {providers.length} Available
                </span>
              </div>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white border-end-0">
                  <FiSearch size={14} className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Filter provider by name..."
                  value={providerSearch}
                  onChange={(e) => setProviderSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="card-body p-2" style={{ maxHeight: "650px", overflowY: "auto" }}>
              {loading && providers.length === 0 ? (
                <div className="text-center py-5">
                  <div className="spinner-border spinner-border-sm text-primary" role="status" />
                  <p className="text-muted small mt-2">Finding verified providers...</p>
                </div>
              ) : providers.length === 0 ? (
                <div className="text-center py-5 text-muted small">
                  No service providers listed in this category yet.
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {providers.map((p) => {
                    const isSelected = selectedProvider?.id === p.id;
                    const fullName = `${p.first_name || ""} ${p.last_name || ""}`.trim() || "Professional";
                    return (
                      <div
                        key={p.id}
                        className={`p-3 rounded-3 border cursor-pointer transition-all ${
                          isSelected
                            ? "border-primary bg-primary bg-opacity-10 shadow-sm"
                            : "bg-white border-light hover-shadow"
                        }`}
                        onClick={() => handleSelectProvider(p)}
                      >
                        <div className="d-flex align-items-start justify-content-between">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="rounded-circle bg-primary bg-opacity-25 text-primary fw-bold d-flex align-items-center justify-content-center"
                              style={{ width: "44px", height: "44px", fontSize: "15px" }}
                            >
                              {fullName.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="fw-bold text-dark">{fullName}</div>
                              <div className="text-muted small">
                                <FiMapPin size={12} className="me-1 text-danger" />
                                {p.city ? `${p.city}, ${p.state || ""}` : "Verified Area"}
                              </div>
                            </div>
                          </div>
                          <FiArrowRight
                            size={16}
                            className={isSelected ? "text-primary" : "text-muted"}
                          />
                        </div>

                        <div className="d-flex gap-3 mt-2 pt-2 border-top border-light text-muted small">
                          {p.mobile_number && (
                            <span className="d-flex align-items-center gap-1">
                              <FiPhone size={12} /> {p.mobile_number}
                            </span>
                          )}
                          {p.email_address && (
                            <span className="d-flex align-items-center gap-1 text-truncate" style={{ maxWidth: "160px" }}>
                              <FiMail size={12} /> {p.email_address}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Provider's Portfolio & Services */}
        <div className="col-12 col-lg-7">
          <div className="card border-0 shadow-sm rounded-3">
            <div className="card-header bg-white border-bottom p-3">
              <h6 className="fw-bold mb-0 text-dark">
                {selectedProvider
                  ? `${selectedProvider.first_name}'s Services & Portfolio`
                  : "Select a provider on the left to view services"}
              </h6>
            </div>

            <div className="card-body p-3">
              {!selectedProvider ? (
                <div className="text-center py-5 text-muted">
                  <FiGrid size={48} className="opacity-50 mb-3" />
                  <p className="small mb-0">
                    Click any verified service provider on the left to explore their services, portfolio, and pricing.
                  </p>
                </div>
              ) : providerServices.length === 0 ? (
                <div className="text-center py-5 text-muted small">
                  No public services posted by this provider currently.
                </div>
              ) : (
                <div className="row g-3">
                  {providerServices.map((svc) => {
                    const photos = svc.service_photos || svc.photos || [];
                    const thumbnail = photos[0]?.url
                      ? `https://images.allhomedesire.com${photos[0].url}`
                      : null;

                    return (
                      <div key={svc.id} className="col-12 col-md-6">
                        <div className="card h-100 border border-light shadow-sm rounded-3 overflow-hidden d-flex flex-column">
                          {thumbnail ? (
                            <img
                              src={thumbnail}
                              alt={svc.title || "Service"}
                              className="w-100 object-fit-cover"
                              style={{ height: "160px" }}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          ) : (
                            <div
                              className="w-100 bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center text-muted"
                              style={{ height: "140px" }}
                            >
                              <FiLayers size={32} />
                            </div>
                          )}

                          <div className="card-body p-3 d-flex flex-column justify-content-between flex-grow-1">
                            <div>
                              <div className="d-flex justify-content-between align-items-start mb-1">
                                <h6 className="fw-bold text-dark text-truncate mb-0" title={svc.title}>
                                  {svc.title || "Home Service"}
                                </h6>
                              </div>
                              <p
                                className="text-muted small mb-2 line-clamp-2"
                                style={{ minHeight: "38px" }}
                              >
                                {svc.description || "High quality professional services for your home."}
                              </p>

                              {svc.price && (
                                <div className="fw-bold text-primary mb-2">
                                  ₹{Number(svc.price).toLocaleString("en-IN")}{" "}
                                  <span className="small text-muted fw-normal">
                                    {svc.price_unit ? `/${svc.price_unit}` : ""}
                                  </span>
                                </div>
                              )}
                            </div>

                            <button
                              className="btn btn-outline-primary btn-sm w-100 d-flex align-items-center justify-content-center gap-1 mt-2"
                              onClick={() => handleOpenDetail(svc.id)}
                            >
                              <FiInfo size={14} /> View Service Details
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================================
          SERVICE DETAIL MODAL
          ========================================================================= */}
      {showDetailModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content border-0 shadow rounded-3">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-dark">
                  {serviceDetail?.title || "Service Details"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDetailModal(false)}
                />
              </div>

              <div className="modal-body p-4">
                {detailLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary spinner-border-sm" role="status" />
                    <p className="text-muted small mt-2">Loading service details...</p>
                  </div>
                ) : serviceDetail ? (
                  <div>
                    {/* Media Gallery */}
                    {serviceDetail.service_photos?.length > 0 && (
                      <div className="d-flex gap-2 overflow-auto pb-2 mb-3">
                        {serviceDetail.service_photos.map((p, idx) => (
                          <img
                            key={idx}
                            src={`https://images.allhomedesire.com${p.url}`}
                            alt="Service Photo"
                            className="rounded-3 object-fit-cover shadow-sm"
                            style={{ width: "200px", height: "140px" }}
                          />
                        ))}
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        {serviceDetail.price && (
                          <h4 className="fw-bold text-primary mb-0">
                            ₹{Number(serviceDetail.price).toLocaleString("en-IN")}{" "}
                            <span className="small text-muted fw-normal">
                              {serviceDetail.price_unit ? `/${serviceDetail.price_unit}` : ""}
                            </span>
                          </h4>
                        )}
                        <span className="badge bg-light text-secondary border mt-1">
                          {serviceDetail.allServiceCategory?.name || selectedCategory?.name || "Service"}
                        </span>
                      </div>

                      <button
                        className={`btn btn-sm d-flex align-items-center gap-1 ${
                          serviceDetail.is_favorite || serviceDetail.favorite
                            ? "btn-danger"
                            : "btn-outline-danger"
                        }`}
                        onClick={handleFavoriteToggle}
                        disabled={favoriteLoading}
                      >
                        <FiHeart size={14} />
                        {serviceDetail.is_favorite || serviceDetail.favorite ? "Favorited" : "Favorite"}
                      </button>
                    </div>

                    <h6 className="fw-bold text-dark mb-1">Description</h6>
                    <p className="text-secondary small mb-4" style={{ whiteSpace: "pre-wrap" }}>
                      {serviceDetail.description || "No description provided."}
                    </p>

                    {/* Provider Info Card */}
                    <div className="card bg-light border-0 p-3 rounded-3 mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fw-bold text-dark">
                            {serviceDetail.partner?.first_name} {serviceDetail.partner?.last_name}
                          </div>
                          <div className="text-muted small">
                            {serviceDetail.partner?.city}, {serviceDetail.partner?.state}
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          {serviceDetail.partner?.mobile_number && (
                            <a
                              href={`tel:${serviceDetail.partner.mobile_number}`}
                              className="btn btn-outline-success btn-sm d-flex align-items-center gap-1"
                            >
                              <FiPhone size={13} /> Call
                            </a>
                          )}
                          <button
                            className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                            onClick={() => {
                              setReminderTitle(`Meeting for ${serviceDetail.title}`);
                              setShowReminderModal(true);
                            }}
                          >
                            <FiCalendar size={13} /> Schedule Meeting
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-muted text-center py-4">Service information unavailable.</div>
                )}
              </div>

              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowDetailModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          SCHEDULE REMINDER / MEETING MODAL
          ========================================================================= */}
      {showReminderModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow rounded-3">
              <form onSubmit={handleScheduleReminder}>
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold text-dark d-flex align-items-center gap-2">
                    <FiCalendar className="text-primary" /> Schedule Service Meeting
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowReminderModal(false)}
                    disabled={reminderSubmitting}
                  />
                </div>
                <div className="modal-body py-3">
                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-dark">Meeting Title *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={reminderTitle}
                      onChange={(e) => setReminderTitle(e.target.value)}
                      required
                    />
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <label className="form-label fw-semibold small text-dark">Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={reminderDate}
                        onChange={(e) => setReminderDate(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-semibold small text-dark">Time *</label>
                      <input
                        type="time"
                        className="form-control"
                        value={reminderTime}
                        onChange={(e) => setReminderTime(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-dark">Notes / Agendas</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Add any specific discussion topics or requirements..."
                      value={reminderNotes}
                      onChange={(e) => setReminderNotes(e.target.value)}
                    />
                  </div>
                </div>

                <div className="modal-footer border-0 pt-0">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setShowReminderModal(false)}
                    disabled={reminderSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm px-4"
                    disabled={reminderSubmitting}
                  >
                    {reminderSubmitting ? "Scheduling..." : "Save Reminder"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
